import React, { useCallback, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Допустимые формы оплаты
const PAYMENT_FORMS = ["С НДС 20%", "С НДС 22%", "Без НДС", "Наличные"];

// Форматирование денег: разделители тысяч и 2 знака после запятой
const fmtMoney = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0,00";
  return n.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
// Парсинг чисел из инпутов: допускаем запятую
const parseNum = (s) => {
  const n = parseFloat(String(s ?? "").replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const EMPTY_EXTRA_ROW = {
  name: "",
  qty: 0,
  unit: "",
  price: 0,
  sum: 0,
  contractor: "",
  payment_form: PAYMENT_FORMS[0],
  paid: 0,
  remain: 0,
  pay_dates: [],
};
const createEmptyRow = () => ({ ...EMPTY_EXTRA_ROW });
const rowHasContent = (row = {}) => {
  if (!row) return false;
  if (String(row.name || "").trim()) return true;
  if (String(row.unit || "").trim()) return true;
  if (String(row.contractor || "").trim()) return true;
  if ((row.payment_form || "") !== PAYMENT_FORMS[0]) return true;
  if (Number(row.qty) > 0) return true;
  if (Number(row.price) > 0) return true;
  if (Number(row.paid) > 0) return true;
  if (Array.isArray(row.pay_dates) && row.pay_dates.length > 0) return true;
  return false;
};

// Форматирование даты из ISO в DD.MM.YYYY
const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
};

/**
 * Всплывающий редактор мульти-дат для одной ячейки
 */
function MultiDateEditor({ value, onChange, onClose, anchorRect }) {
  const [dates, setDates] = useState(Array.isArray(value) ? value : []);
  const [newDate, setNewDate] = useState("");

  const add = () => {
    if (!newDate) return;
    const iso = newDate; // YYYY-MM-DD
    if (!dates.includes(iso)) setDates((a) => [...a, iso]);
    setNewDate("");
  };
  const remove = (iso) => setDates((a) => a.filter((x) => x !== iso));
  const save = () => {
    let nextDates = dates;
    if (newDate) {
      const iso = newDate;
      if (!nextDates.includes(iso)) nextDates = [...nextDates, iso];
    }
    const uniqueSorted = Array.from(new Set(nextDates)).sort();
    onChange(uniqueSorted);
    onClose();
  };

  // clamp popup to viewport so buttons remain accessible
  const MAX_W = 440;
  const MAX_H = 360;
  const MARGIN = 8;
  const calcLeft = () => {
    const baseLeft = Math.round(anchorRect.left);
    const minLeft = MARGIN;
    const maxLeft = Math.max(minLeft, window.innerWidth - MAX_W - MARGIN);
    return Math.min(Math.max(baseLeft, minLeft), maxLeft);
  };
  const calcTop = () => {
    // try below anchor, else place above
    const below = Math.round(anchorRect.bottom + 6);
    if (below + MAX_H + MARGIN <= window.innerHeight) return below;
    const above = Math.round(anchorRect.top - MAX_H - 6);
    return Math.max(MARGIN, above);
  };

  const style = {
    position: "fixed",
    top: calcTop(),
    left: calcLeft(),
    minWidth: Math.round(Math.max(280, anchorRect.width)),
    maxWidth: MAX_W,
    maxHeight: MAX_H,
    overflowY: "auto",
    background: "#fff",
    border: "1px solid #ccc",
    borderRadius: 8,
    boxShadow: "0 12px 28px rgba(0,0,0,.2)",
    zIndex: 9999,
    padding: 12,
  };

  return createPortal(
    <div
      style={style}
      role="dialog"
      aria-label="Редактор дат выплат"
      tabIndex={-1}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
        if (e.key === "Enter") {
          // If focus is on date input, let it trigger add first
          const active = document.activeElement;
          if (active && active.type === "date") {
            // do nothing here, allow input Enter to add via onKeyDown below
            return;
          }
          save();
        }
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 8 }}>Даты выплат</div>

      {dates.length === 0 ? (
        <div style={{ color: "#777", marginBottom: 8 }}>Нет дат</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 8 }}>
          {dates.map((iso) => (
            <div key={iso} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ flex: 1 }}>{fmtDate(iso)}</div>
              <button
                type="button"
                onClick={() => remove(iso)}
                style={{ border: "none", background: "none", color: "#c00", cursor: "pointer" }}
                title="Удалить дату"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
        <input
          type="date"
          value={newDate}
          onChange={(e) => setNewDate(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          autoFocus
          style={{ padding: "6px 8px", border: "1px solid #ccc", borderRadius: 6 }}
        />
        <button
          type="button"
          onClick={add}
          style={{ padding: "6px 10px", border: "1px solid #ccc", borderRadius: 6, background: "#f5f5f5", cursor: "pointer" }}
        >
          Добавить
        </button>
      </div>

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button
          type="button"
          onClick={onClose}
          style={{ padding: "6px 12px", border: "1px solid #ccc", background: "#fff", borderRadius: 6, cursor: "pointer" }}
        >
          Отмена
        </button>
        <button
          type="button"
          onClick={save}
          style={{ padding: "6px 12px", border: "1px solid #c59bff", background: "#f5efff", color: "#5427b0", borderRadius: 6, cursor: "pointer", fontWeight: 700 }}
        >
          ОК
        </button>
      </div>
    </div>,
    document.body
  );
}

/**
 * Вкладка «Доп. затраты»
 * props:
 *  - data: массив строк затрат
 *  - onChange(nextRows): сохранить массив
 */
export default function ExtrasSheet({ data, onChange, readOnlyLocked = false, isSuper = false }) {
  const rows = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const [draftRow, setDraftRow] = useState(createEmptyRow());
  const draftRowRef = useRef(draftRow);
  const rowsWithDraft = useMemo(() => {
    if (readOnlyLocked) return rows;
    return [...rows, { ...draftRow, _isDraft: true }];
  }, [rows, draftRow, readOnlyLocked]);

  // Пересчёт одной строки
  const recalcRow = (row) => {
    const qty = Number(row.qty) || 0;
    const price = Number(row.price) || 0;
    const sum = round2(qty * price);
    const paid = Number(row.paid) || 0;
    const remain = round2(sum - paid);
    return { ...row, sum, remain };
  };

  const applyRow = (idx, mutator, options = {}) => {
    const { allowWhenLocked = false, commitOpts } = options;
    if (readOnlyLocked && !allowWhenLocked) return;
    if (idx >= rows.length) {
      setDraftRow((prev) => {
        const next = recalcRow(mutator({ ...prev }));
        draftRowRef.current = next;
        return next;
      });
      return;
    }
    const next = rows.map((r, i) => (i === idx ? recalcRow(mutator({ ...r })) : r));
    onChange(next, commitOpts);
  };

  const commitDraftRow = useCallback(
    (commitOpts) => {
      if (readOnlyLocked) return;
      const currentDraft = draftRowRef.current;
      if (!rowHasContent(currentDraft)) return;
      const nextRow = recalcRow({ ...currentDraft });
      onChange([...rows, nextRow], commitOpts);
        const empty = createEmptyRow();
        draftRowRef.current = empty;
        setDraftRow(empty);
    },
    [onChange, readOnlyLocked, rows]
  );

  const handleDraftBlur = (event) => {
    if (readOnlyLocked) return;
    if (!rowHasContent(draftRowRef.current)) return;
    if (dateEditIdx === rows.length) return;
    const stillInRow = event?.currentTarget?.contains(event?.relatedTarget);
    if (stillInRow) return;
    commitDraftRow({ forceCommit: true });
  };

  const removeRow = (idx) => {
    if (readOnlyLocked) return;
    onChange(rows.filter((_, i) => i !== idx), { forceCommit: true });
  };

  // Итоги: считаем по Сумма, Выплачено, Осталось
  const totals = useMemo(() => {
    const isVatPayment = (form) => typeof form === "string" && form.startsWith("С НДС");
    const acc = {
      vat: { sum: 0, paid: 0, remain: 0 },
      noVat: { sum: 0, paid: 0, remain: 0 },
      cash: { sum: 0, paid: 0, remain: 0 },
      all: { sum: 0, paid: 0, remain: 0 },
    };
    for (const r of rows) {
      const s = Number(r.sum) || 0;
      const p = Number(r.paid) || 0;
      const rm = Number(r.remain) || 0;
      const form = r.payment_form;

      acc.all.sum += s; acc.all.paid += p; acc.all.remain += rm;
      if (isVatPayment(form)) {
        acc.vat.sum += s;
        acc.vat.paid += p;
        acc.vat.remain += rm;
      } else if (form === "Без НДС") {
        acc.noVat.sum += s;
        acc.noVat.paid += p;
        acc.noVat.remain += rm;
      } else if (form === "Наличные") {
        acc.cash.sum += s;
        acc.cash.paid += p;
        acc.cash.remain += rm;
      }
    }
    const roundObj = (o) => ({
      sum: round2(o.sum),
      paid: round2(o.paid),
      remain: round2(o.remain),
    });
    return {
      vat: roundObj(acc.vat),
      noVat: roundObj(acc.noVat),
      cash: roundObj(acc.cash),
      all: roundObj(acc.all),
    };
  }, [rows]);

  // Мульти-редактор дат
  const [dateEditIdx, setDateEditIdx] = useState(null);
  const [anchorRect, setAnchorRect] = useState(null);

  const openDateEditor = (idx, el) => {
    setDateEditIdx(idx);
    if (el) setAnchorRect(el.getBoundingClientRect());
  };
  const closeDateEditor = () => {
    const editingDraft = dateEditIdx === rows.length;
    setDateEditIdx(null);
    setAnchorRect(null);
    if (editingDraft) commitDraftRow({ forceCommit: true });
  };

  // Локальное состояние: какой инпут сейчас в режиме редактирования,
  // чтобы при фокусе показывать «сырой» текст, а вне фокуса — форматировать с разделителями и сотыми
  const [cellDraft, setCellDraft] = useState({ idx: null, field: null, value: "" });
  const startCellEdit = (idx, field, initial = "") => {
    setCellDraft({ idx, field, value: initial });
  };
  const updateCellDraft = (idx, field, value) => {
    setCellDraft((prev) => (prev.idx === idx && prev.field === field ? { ...prev, value } : prev));
  };
  const finishCellEdit = (idx, field, parser, options) => {
    setCellDraft((prev) => {
      if (prev.idx !== idx || prev.field !== field) return prev;
      const parsed = typeof parser === "function" ? parser(prev.value) : prev.value;
      applyRow(idx, (row) => ({ ...row, [field]: parsed }), options);
      return { idx: null, field: null, value: "" };
    });
  };
  const toInputString = (value) => {
    if (value === null || value === undefined) return "";
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    return String(num).replace(".", ",");
  };
  const sanitizeNumeric = (value) => String(value || "").replace(/[^0-9.,-]/g, "");

  // Стили таблицы и ячеек
  const tableWrapperStyle = {
    background: "var(--bg-card, #fff)",
    borderRadius: 18,
    border: "1px solid var(--border-200, #e6e8ef)",
    boxShadow: "var(--shadow-1, 0 18px 36px rgba(15,23,42,0.08))",
    overflow: "hidden",
    overflowX: "auto",
  };
  const tableStyle = {
    borderCollapse: "collapse",
    width: "100%",
    tableLayout: "fixed",
    minWidth: 780,
  };
  const base = {
    border: "1px solid #dfe3eb",
    padding: "10px 8px",
    fontSize: "0.95rem",
    background: "var(--bg-card, #fff)",
    verticalAlign: "middle",
    whiteSpace: "normal",
    wordBreak: "break-word",
    lineHeight: 1.35,
  };
  const th = {
    ...base,
    background: "linear-gradient(135deg, var(--brand-50, #f5efff), var(--brand-100, #eadcff))",
    fontWeight: 700,
    textAlign: "center",
  };
  const ro = { ...base, background: "#f9fafc", textAlign: "left" };
  const roCenter = { ...ro, textAlign: "center" };
  const roRight = { ...ro, textAlign: "right", paddingRight: 10 };
  const edit = { ...base, background: "#fff" };
  const editCenter = { ...edit, textAlign: "center" };
  const editRight = { ...edit, textAlign: "right" };
  const inputBase = { width: "100%", padding: "6px 2px 4px", border: "none", borderBottom: "1px solid rgba(84,39,176,0.3)", background: "transparent", fontSize: "0.95rem", lineHeight: 1.3, boxSizing: "border-box" };
  const input = { ...inputBase, textAlign: "left" };
  const inputCenter = { ...inputBase, textAlign: "center" };
  const inputRight = { ...inputBase, textAlign: "right" };
  const disabledLine = { borderBottom: "1px solid #dfe3eb", color: "#94a3b8", cursor: "not-allowed" };
  const delBtn = { border: "none", background: "none", color: "#c00", fontSize: 18, cursor: "pointer" };

  // Цвет фона для «Осталось выплатить»
  const remainBg = (remain) => {
    const r = Number(remain) || 0;
    const eps = 0.005;
    if (r > eps) return "#ffd9d9"; // красный
    if (r < -eps) return "#ffe9cc"; // оранжевый
    return "#daf5d7"; // зелёный
  };

  return (
    <div style={{ width: "100%" }}>
      <div style={{ marginBottom: 12, fontSize: 13, color: "#6b7280", textAlign: "right" }}>
        {readOnlyLocked
          ? "Карточка подписана — редактирование недоступно."
          : "Последняя строка всегда пустая — начните ввод, и она сохранится автоматически."}
      </div>

      <div style={tableWrapperStyle}>
        <table style={tableStyle}>
        <colgroup>
          <col style={{ width: "5%" }} />   {/* № */}
          <col style={{ width: "24%" }} />  {/* Наименование */}
          <col style={{ width: "7%" }} />   {/* Кол-во */}
          <col style={{ width: "8%" }} />   {/* Ед. изм. */}
          <col style={{ width: "10%" }} />  {/* Цена за ед. */}
          <col style={{ width: "10%" }} />  {/* Сумма (RO) */}
          <col style={{ width: "12%" }} />  {/* Подрядчик */}
          <col style={{ width: "12%" }} />  {/* Форма оплаты */}
          <col style={{ width: "12%" }} />  {/* Выплачено */}
          <col style={{ width: "12%" }} />  {/* Осталось выплатить (RO) */}
          <col style={{ width: "12%" }} />  {/* Дата выплаты (мульти) */}
          <col style={{ width: "4%" }} />   {/* удалить */}
        </colgroup>

        <thead>
          <tr>
            <th style={th}>№</th>
            <th style={th}>Наименование</th>
            <th style={th}>Кол.-во</th>
            <th style={th}>Ед. изм.</th>
            <th style={th}>Цена за ед.</th>
            <th style={th}>Сумма</th>
            <th style={th}>Подрядчик</th>
            <th style={th}>Форма оплаты затрат</th>
            <th style={th}>Выплачено</th>
            <th style={th}>Осталось выплатить</th>
            <th style={th}>Дата выплаты</th>
            <th style={th}></th>
          </tr>
        </thead>

        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={12} style={{ ...ro, textAlign: "center", color: "#99a1b7" }}>
                Нет позиций. Заполните строку ниже — она сохранится автоматически.
              </td>
            </tr>
          )}

          {rowsWithDraft.map((row, idx) => {
            const isDraft = row._isDraft;
            const draftEmpty = isDraft && !rowHasContent(row);
            const rowKey = isDraft ? "extras-draft" : idx;
            const allowPaidOverride = readOnlyLocked && isSuper;
            const paidApplyOpts = allowPaidOverride ? { allowWhenLocked: true } : undefined;

            return (
              <tr key={rowKey} onBlurCapture={isDraft ? handleDraftBlur : undefined}>
                <td style={isDraft ? { ...roCenter, color: "#a3aab7" } : roCenter}>{idx + 1}</td>

                <td style={readOnlyLocked ? ro : edit}>
                  <input
                    type="text"
                    value={row.name ?? ""}
                    onChange={(e) => applyRow(idx, (r) => ({ ...r, name: e.target.value }))}
                    disabled={readOnlyLocked}
                    style={readOnlyLocked ? { ...input, ...disabledLine } : input}
                  />
                </td>

                <td style={readOnlyLocked ? roCenter : editCenter}>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={
                      cellDraft.idx === idx && cellDraft.field === "qty"
                        ? cellDraft.value
                        : (isDraft && draftEmpty ? "" : fmtMoney(row.qty))
                    }
                    onFocus={(e) => {
                      if (readOnlyLocked) return;
                      e.target.select();
                      startCellEdit(idx, "qty", toInputString(row.qty));
                    }}
                    onChange={(e) => {
                      if (readOnlyLocked) return;
                      const nextVal = sanitizeNumeric(e.target.value);
                      if (cellDraft.idx !== idx || cellDraft.field !== "qty") {
                        startCellEdit(idx, "qty", nextVal);
                      } else {
                        updateCellDraft(idx, "qty", nextVal);
                      }
                    }}
                    onBlur={() => {
                      if (readOnlyLocked) return;
                      finishCellEdit(idx, "qty", parseNum);
                    }}
                    disabled={readOnlyLocked}
                    style={readOnlyLocked ? { ...inputCenter, ...disabledLine } : inputCenter}
                  />
                </td>

                <td style={readOnlyLocked ? roCenter : editCenter}>
                  <input
                    type="text"
                    value={row.unit ?? ""}
                    onChange={(e) => applyRow(idx, (r) => ({ ...r, unit: e.target.value }))}
                    disabled={readOnlyLocked}
                    style={readOnlyLocked ? { ...inputCenter, ...disabledLine } : inputCenter}
                  />
                </td>

                <td style={readOnlyLocked ? roRight : editRight}>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={
                      cellDraft.idx === idx && cellDraft.field === "price"
                        ? cellDraft.value
                        : (isDraft && draftEmpty ? "" : fmtMoney(row.price))
                    }
                    onFocus={(e) => {
                      if (readOnlyLocked) return;
                      e.target.select();
                      startCellEdit(idx, "price", toInputString(row.price));
                    }}
                    onChange={(e) => {
                      if (readOnlyLocked) return;
                      const nextVal = sanitizeNumeric(e.target.value);
                      if (cellDraft.idx !== idx || cellDraft.field !== "price") {
                        startCellEdit(idx, "price", nextVal);
                      } else {
                        updateCellDraft(idx, "price", nextVal);
                      }
                    }}
                    onBlur={() => {
                      if (readOnlyLocked) return;
                      finishCellEdit(idx, "price", parseNum);
                    }}
                    disabled={readOnlyLocked}
                    style={readOnlyLocked ? { ...inputRight, ...disabledLine } : inputRight}
                  />
                </td>

                <td style={roRight}>{draftEmpty ? "—" : fmtMoney(row.sum)}</td>

                <td style={readOnlyLocked ? ro : edit}>
                  <input
                    type="text"
                    value={row.contractor ?? ""}
                    onChange={(e) => applyRow(idx, (r) => ({ ...r, contractor: e.target.value }))}
                    disabled={readOnlyLocked}
                    style={readOnlyLocked ? { ...input, ...disabledLine } : input}
                  />
                </td>

                <td style={readOnlyLocked ? ro : edit}>
                  <select
                    value={row.payment_form ?? PAYMENT_FORMS[0]}
                    onChange={(e) => applyRow(idx, (r) => ({ ...r, payment_form: e.target.value }))}
                    disabled={readOnlyLocked}
                    style={{
                      ...(readOnlyLocked ? { ...input, ...disabledLine } : input),
                      paddingRight: 28,
                      background: readOnlyLocked ? '#f3f4f6' : 'transparent',
                      appearance: 'none',
                    }}
                  >
                    {PAYMENT_FORMS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </td>

                <td style={isSuper || !readOnlyLocked ? editRight : roRight}>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={
                      cellDraft.idx === idx && cellDraft.field === "paid"
                        ? cellDraft.value
                        : (isDraft && draftEmpty ? "" : fmtMoney(row.paid))
                    }
                    onFocus={(e) => {
                      if (!(isSuper || !readOnlyLocked)) return;
                      e.target.select();
                      startCellEdit(idx, "paid", toInputString(row.paid));
                    }}
                    onChange={(e) => {
                      if (!(isSuper || !readOnlyLocked)) return;
                      const nextVal = sanitizeNumeric(e.target.value);
                      if (cellDraft.idx !== idx || cellDraft.field !== "paid") {
                        startCellEdit(idx, "paid", nextVal);
                      } else {
                        updateCellDraft(idx, "paid", nextVal);
                      }
                    }}
                    onBlur={() => {
                      if (!(isSuper || !readOnlyLocked)) return;
                      finishCellEdit(idx, "paid", parseNum, paidApplyOpts);
                    }}
                    disabled={readOnlyLocked && !isSuper}
                    style={readOnlyLocked && !isSuper ? { ...inputRight, ...disabledLine } : inputRight}
                  />
                </td>

                <td style={{ ...roRight, background: draftEmpty ? '#f9fafc' : remainBg(row.remain) }}>{draftEmpty ? "—" : fmtMoney(row.remain)}</td>

                <td
                  style={readOnlyLocked && !isSuper ? ro : edit}
                  onClick={(e) => { if (isSuper || !readOnlyLocked) openDateEditor(idx, e.currentTarget); }}
                  title={readOnlyLocked && !isSuper ? "Доступно только суперпользователям после подписания" : "Кликните, чтобы указать одну или несколько дат выплат"}
                >
                  <div style={{ width: "100%", cursor: isSuper || !readOnlyLocked ? "pointer" : "default", minHeight: 24, color: draftEmpty ? "#a3aab7" : undefined }}>
                    {Array.isArray(row.pay_dates) && row.pay_dates.length > 0
                      ? row.pay_dates.map(fmtDate).join(", ")
                      : "—"}
                  </div>
                </td>

                <td style={roCenter}>
                  {!isDraft && (
                    <button
                      type="button"
                      onClick={() => removeRow(idx)}
                      disabled={readOnlyLocked}
                      style={{ ...delBtn, color: readOnlyLocked ? '#999' : '#c00', cursor: readOnlyLocked ? 'not-allowed' : 'pointer' }}
                      title={readOnlyLocked ? 'Удаление недоступно: карточка подписана' : "Удалить строку"}
                    >
                      🗑️
                    </button>
                  )}
                </td>
              </tr>
            );
          })}

          {rows.length > 0 && (
            <>
              <tr>
                <td style={ro} />
                <td style={{ ...ro, fontWeight: 800 }}>ИТОГО с НДС:</td>
                <td style={ro} />
                <td style={ro} />
                <td style={ro} />
                {/* Сумма */}
                <td style={{ ...roRight, fontWeight: 800 }}>{fmtMoney(totals.vat.sum)}</td>
                <td style={ro} />
                <td style={ro} />
                {/* Выплачено */}
                <td style={{ ...roRight, fontWeight: 800 }}>{fmtMoney(totals.vat.paid)}</td>
                {/* Осталось */}
                <td style={{ ...roRight, fontWeight: 800, background: remainBg(totals.vat.remain) }}>
                  {fmtMoney(totals.vat.remain)}
                </td>
                <td style={ro} />
                <td style={ro} />
              </tr>
              <tr>
                <td style={ro} />
                <td style={{ ...ro, fontWeight: 800 }}>ИТОГО без НДС:</td>
                <td style={ro} />
                <td style={ro} />
                <td style={ro} />
                <td style={{ ...roRight, fontWeight: 800 }}>{fmtMoney(totals.noVat.sum)}</td>
                <td style={ro} />
                <td style={ro} />
                <td style={{ ...roRight, fontWeight: 800 }}>{fmtMoney(totals.noVat.paid)}</td>
                <td style={{ ...roRight, fontWeight: 800, background: remainBg(totals.noVat.remain) }}>
                  {fmtMoney(totals.noVat.remain)}
                </td>
                <td style={ro} />
                <td style={ro} />
              </tr>
              <tr>
                <td style={ro} />
                <td style={{ ...ro, fontWeight: 800 }}>ИТОГО Наличные:</td>
                <td style={ro} />
                <td style={ro} />
                <td style={ro} />
                <td style={{ ...roRight, fontWeight: 800 }}>{fmtMoney(totals.cash.sum)}</td>
                <td style={ro} />
                <td style={ro} />
                <td style={{ ...roRight, fontWeight: 800 }}>{fmtMoney(totals.cash.paid)}</td>
                <td style={{ ...roRight, fontWeight: 800, background: remainBg(totals.cash.remain) }}>
                  {fmtMoney(totals.cash.remain)}
                </td>
                <td style={ro} />
                <td style={ro} />
              </tr>
              <tr>
                <td style={ro} />
                <td style={{ ...ro, fontWeight: 800 }}>ВСЕГО по всем позициям:</td>
                <td style={ro} />
                <td style={ro} />
                <td style={ro} />
                <td style={{ ...roRight, fontWeight: 800 }}>{fmtMoney(totals.all.sum)}</td>
                <td style={ro} />
                <td style={ro} />
                <td style={{ ...roRight, fontWeight: 800 }}>{fmtMoney(totals.all.paid)}</td>
                <td style={{ ...roRight, fontWeight: 800, background: remainBg(totals.all.remain) }}>
                  {fmtMoney(totals.all.remain)}
                </td>
                <td style={ro} />
                <td style={ro} />
              </tr>
            </>
          )}
        </tbody>
      </table>
      </div>

      {/* Всплывающее окно мульти-дат */}
      {dateEditIdx != null && anchorRect && (
        <MultiDateEditor
          value={rowsWithDraft[dateEditIdx]?.pay_dates}
          anchorRect={anchorRect}
          onChange={(dates) =>
            applyRow(dateEditIdx, (r) => ({ ...r, pay_dates: dates }), {
              allowWhenLocked: readOnlyLocked && isSuper,
              commitOpts: { forceCommit: true },
            })
          }
          onClose={closeDateEditor}
        />
      )}
    </div>
  );
}